import { Accordion } from "@/components/ui/accordion";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@radix-ui/react-accordion";
import { Plug } from "lucide-react";
import type { FpMessagePart } from "../../../../../schemas/codegen";
import { MarkdownSkeleton } from "../../Markdown";
import { SourceCode } from "../../SourceCode";
import { ExpandableMessageCard, SimpleMessageCard, ToolError } from "./shared";

interface GenericToolProps {
  part: Extract<FpMessagePart, { type: "tool-call" }>;
}

/**
 * Extract possible mcp tool name from the part.toolName
 *
 * Example toolNames are:
 * - dJIqnG7a_list_notion_databases
 * - dJIqnG7a_query_database_rows
 *
 * Where `dJIqnG7a` is the serverId and `list_notion_databases` is the name.
 **/
function extractToolNameInformation(toolName: string) {
  const serverId = toolName.split("_")[0];
  const name = toolName.split("_").slice(1).join("_");

  // Validate that the serverId matches the expected format
  if (!/^[a-zA-Z0-9_-]+$/.test(serverId)) {
    return {
      serverId: null,
      name: toolName,
    };
  }

  return { serverId, name };
}

const McpTool = ({
  part,
  name,
  serverId,
}: GenericToolProps & { name: string; serverId: string }) => {
  const hasArgs = part.args && Object.keys(part.args).length > 0;
  const hasResult = part.result && Object.keys(part.result).length > 0;

  const displayName = (
    <div className="flex items-center gap-2">
      {name}
      <span className="text-muted-foreground">Server: {serverId}</span>
    </div>
  );

  if (!hasArgs && !hasResult) {
    return (
      <SimpleMessageCard status={part.status} icon={Plug} title={displayName} />
    );
  }

  const accordionType = hasArgs && hasResult ? "multiple" : "single";

  const content = (
    <>
      {hasArgs && part.args && (
        <AccordionItem value="args" className="border rounded-md">
          <AccordionTrigger className="hover:no-underline px-3 py-2 hover:bg-muted/30 transition-colors text-sm w-full text-start">
            Args
          </AccordionTrigger>
          <AccordionContent className="min-w-0 ">
            <SourceCode
              content={JSON.stringify(part.args, null, 2)}
              enableOverflow
              SkeletonComponent={MarkdownSkeleton}
              language="json"
            />
          </AccordionContent>
        </AccordionItem>
      )}
      {hasResult && part.result ? (
        <AccordionItem value="result" className="border rounded-md">
          <AccordionTrigger className="hover:no-underline px-3 py-2 hover:bg-muted/30 transition-colors text-sm w-full text-start">
            Result
          </AccordionTrigger>
          <AccordionContent className="min-w-0">
            {part.status === "complete" && hasResult && part.result ? (
              <SourceCode
                content={JSON.stringify(part.result, null, 2)}
                enableOverflow
                SkeletonComponent={MarkdownSkeleton}
                language="json"
              />
            ) : null}
            {part.status === "error" && <ToolError result={part.result} />}
          </AccordionContent>
        </AccordionItem>
      ) : null}
    </>
  );

  return (
    <ExpandableMessageCard
      status={part.status}
      result={part.result}
      icon={Plug}
      title={displayName}
      accordionValue="generic-tool"
      className="min-w-0"
    >
      {accordionType === "single" ? (
        <Accordion
          type="single"
          collapsible
          className="min-w-0"
          defaultValue={hasArgs ? "args" : "result"}
        >
          {content}
        </Accordion>
      ) : (
        <Accordion
          type="multiple"
          className="min-w-0"
          defaultValue={[hasResult ? "result" : "args"]}
        >
          {content}
        </Accordion>
      )}
    </ExpandableMessageCard>
  );
};

export function GenericTool({ part }: GenericToolProps) {
  const isError = part.status === "error";

  const { serverId, name } = extractToolNameInformation(part.toolName);
  if (serverId && name) {
    return <McpTool part={part} name={name} serverId={serverId} />;
  }

  const activity =
    part.status === "pending"
      ? "Calling"
      : part.status === "complete"
        ? "Called"
        : "Failed:";
  const displayName = (
    <span className="inline-flex items-center gap-2">
      <span className="text-muted-foreground">{activity}</span>
      <span>{part.toolName}</span>
    </span>
  );

  if (isError) {
    return (
      <ExpandableMessageCard
        status={part.status}
        result={part.result}
        icon={Plug}
        title={displayName}
        accordionValue="generic-tool"
      >
        {part.result ? <ToolError result={part.result} /> : null}
      </ExpandableMessageCard>
    );
  }

  return (
    <SimpleMessageCard status={part.status} icon={Plug} title={displayName} />
  );
}
